import React, { useRef, useState } from "react";

import { isMessageKey } from "common/i18n/types";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { Header } from "../components/layout/header";
import { RedirectProgressBar } from "../components/redirect-progress-bar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/auth-context";
import { useTranslations } from "../hooks/use-translations";

import type { RedirectProgressBarHandle } from "../components/redirect-progress-bar";

export const ResetPasswordPage = (): React.JSX.Element => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState(``);
  const [confirmPassword, setConfirmPassword] = useState(``);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(``);
  const [success, setSuccess] = useState(``);
  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectStarted, setRedirectStarted] = useState(false);
  const redirectRef = useRef<RedirectProgressBarHandle | null>(null);

  const { resetPassword, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { translations } = useTranslations();

  const email = searchParams.get(`email`);
  const resetToken = searchParams.get(`resetToken`);

  if (isAuthenticated) {
    return <Navigate replace to="/account/data" />;
  }

  if (!email || !resetToken) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <Card data-testid="reset-password-error-card">
              <CardHeader>
                <CardTitle className="text-center">{translations.resetPassword.invalidLinkTitle}</CardTitle>
                <CardDescription className="text-center">
                  {translations.resetPassword.invalidLinkDescription}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                  {translations.resetPassword.invalidToken}
                </div>
                <div className="space-y-2">
                  <Link to="/forgot-password">
                    <Button className="w-full">{translations.resetPassword.requestNewReset}</Button>
                  </Link>
                  <Link to="/login">
                    <Button className="w-full" variant="outline">
                      {translations.common.backToSignIn}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(``);
    setSuccess(``);

    if (newPassword !== confirmPassword) {
      setError(translations.common.passwordsDoNotMatch);
      setIsLoading(false);
      return;
    }

    try {
      await resetPassword(email, resetToken, newPassword);
      setSuccess(translations.resetPassword.success);
      setShowRedirect(true);
      setRedirectStarted(false);
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage = err.message;

        if (errorMessage === `unexpectedError` || errorMessage.includes(`Internal server error`)) {
          setError(translations.common.serverError);
        } else if (
          errorMessage === `networkError` ||
          errorMessage.includes(`Network error`) ||
          errorMessage.includes(`fetch`)
        ) {
          setError(translations.common.networkError);
        } else if (isMessageKey(errorMessage)) {
          setError(translations.auth[errorMessage]);
        } else {
          setError(errorMessage);
        }
      } else {
        setError(translations.resetPassword.failure);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualRedirect = () => {
    navigate(`/login`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card data-testid="reset-password-card">
            <CardHeader>
              <CardTitle className="text-center">{translations.resetPassword.title}</CardTitle>
              <CardDescription className="text-center">
                {translations.resetPassword.description.replace(`{email}`, email ?? ``)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {success ? (
                <div className="text-center space-y-4">
                  <div
                    className="text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md"
                    data-testid="reset-success"
                  >
                    {success}
                  </div>
                  {showRedirect ? (
                    <RedirectProgressBar
                      ref={(instance) => {
                        redirectRef.current = instance;

                        if (instance && showRedirect && !redirectStarted) {
                          instance.start();
                          setRedirectStarted(true);
                        }
                      }}
                      redirectUrl="/login"
                      title={translations.redirects.toLoginAfterResetTitle}
                      onCancel={() => {
                        setShowRedirect(false);
                        setRedirectStarted(false);
                      }}
                    />
                  ) : null}
                  <Button className="w-full" onClick={handleManualRedirect}>
                    {translations.resetPassword.continueNow}
                  </Button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  {error ? (
                    <div
                      className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
                      data-testid="reset-error"
                    >
                      {error}
                    </div>
                  ) : null}

                  <div>
                    <Input
                      disabled
                      readOnly
                      className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      label={translations.common.emailLabel}
                      name="email"
                      type="email"
                      value={email || ``}
                    />
                  </div>

                  <div>
                    <Input
                      disabled
                      readOnly
                      className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-mono text-xs"
                      label={translations.common.resetTokenLabel}
                      name="resetToken"
                      type="text"
                      value={resetToken || ``}
                    />
                  </div>

                  <div>
                    <Input
                      required
                      data-testid="new-password-input"
                      disabled={isLoading}
                      label={translations.common.newPasswordLabel}
                      name="newPassword"
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={translations.common.newPasswordPlaceholder}
                      type="password"
                      value={newPassword}
                    />
                  </div>

                  <div>
                    <Input
                      required
                      data-testid="confirm-password-input"
                      disabled={isLoading}
                      label={translations.common.confirmNewPasswordLabel}
                      name="confirmPassword"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={translations.common.confirmNewPasswordPlaceholder}
                      type="password"
                      value={confirmPassword}
                    />
                  </div>

                  <Button
                    className="w-full"
                    data-testid="reset-submit"
                    disabled={isLoading || !newPassword || !confirmPassword}
                    isLoading={isLoading}
                    loadingText={translations.common.loading}
                    type="submit"
                  >
                    {translations.resetPassword.resetPasswordButton}
                  </Button>

                  <div className="text-center">
                    <Link
                      className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                      to="/login"
                    >
                      {translations.common.backToSignIn}
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
